import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Card,
  CardContent,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  IconButton,
  Tooltip,
  Badge,
  Tabs,
  Tab,
} from '@mui/material';
import {
  EmojiEvents,
  People,
  Schedule,
  Visibility,
  Search,
  Refresh,
  Share,
} from '@mui/icons-material';
import apiService from '../services/api';
import { Tournament } from '../types';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tournaments-tabpanel-${index}`}
      aria-labelledby={`tournaments-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export const TournamentsPage: React.FC = () => {
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [openTournamentDialog, setOpenTournamentDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [copySuccessId, setCopySuccessId] = useState<number | null>(null);

  const handleCopyLink = (tournamentId: number) => {
    const url = `${window.location.origin}/t/${tournamentId}`;
    navigator.clipboard.writeText(url).then(() => {
      setCopySuccessId(tournamentId);
      setTimeout(() => setCopySuccessId(null), 2000);
    });
  };

  useEffect(() => {
    fetchTournaments();
  }, []);

  const fetchTournaments = async () => {
    try {
      setLoading(true);
      setError('');
      const response = await apiService.getTournaments();
      console.log('Tournaments response:', response);
      // Handle both array and object with data property
      const data = response.data as any;
      const tournamentsData = Array.isArray(data) 
        ? data 
        : (data?.data || data || []);
      setTournaments(tournamentsData);
      console.log('Set tournaments:', tournamentsData);
    } catch (error: any) {
      console.error('Error fetching tournaments:', error);
      console.error('Error details:', error.response?.data);
      setError('Failed to load tournaments. Please try again.');
      setTournaments([]);
    } finally {
      setLoading(false);
    }
  };

  const handleJoinTournament = async (tournamentId: number) => {
    try {
      await apiService.joinTournament(tournamentId);
      fetchTournaments(); // Refresh tournaments
    } catch (error) {
      console.error('Error joining tournament:', error);
    }
  };

  const handleLeaveTournament = async (tournamentId: number) => {
    try {
      await apiService.leaveTournament(tournamentId);
      fetchTournaments(); // Refresh tournaments
    } catch (error) {
      console.error('Error leaving tournament:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'registration':
        return 'success';
      case 'active':
        return 'warning';
      case 'completed':
        return 'info';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getFormatColor = (format: string) => {
    switch (format.toLowerCase()) {
      case 'team':
        return 'primary';
      case 'individual':
        return 'secondary';
      case 'flex_league':
        return 'success';
      default:
        return 'default';
    }
  };

  const filteredTournaments = tournaments.filter(tournament => {
    if (!tournament) return false;
    
    const matchesSearch = searchTerm === '' ||
      (tournament.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (tournament.description || '').toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = statusFilter === 'all' || tournament.status === statusFilter;
    
    return matchesSearch && matchesStatus;
  });

  const openTournaments = filteredTournaments.filter(tournament => 
    tournament.status === 'registration'
  );
  const myTournaments = filteredTournaments.filter(tournament => 
    tournament.status === 'active' // Simple filter for now
  );
  const allTournaments = filteredTournaments;

  const renderTournamentCard = (tournament: Tournament) => {
    return (
      <Card key={tournament.id} sx={{ mb: 2 }}>
        <CardContent>
          <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
            <Box>
              <Typography variant="h6" gutterBottom>
                {tournament.name}
              </Typography>
              <Box display="flex" alignItems="center" gap={1} mb={1}>
        <Chip 
                  label={tournament.status}
                  color={getStatusColor(tournament.status) as any}
          size="small"
        />
        <Chip 
                  label={(tournament.format || 'round_robin').replace('_', ' ')}
                  color={getFormatColor(tournament.format || 'round_robin') as any}
                  variant="outlined"
          size="small"
        />
              </Box>
            </Box>
            <Box display="flex" gap={1}>
              <Tooltip title={copySuccessId === tournament.id ? 'Link copied!' : 'Copy link'}>
                <IconButton size="small" onClick={() => handleCopyLink(tournament.id)}>
                  <Share />
                </IconButton>
              </Tooltip>
              <Tooltip title="View Details">
                <IconButton size="small" component={Link} to={`/t/${tournament.id}`}>
                  <Visibility />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Typography variant="body2" color="text.secondary" paragraph>
            {tournament.description || 'No description available'}
          </Typography>

          <Box display="flex" alignItems="center" gap={1} mb={1}>
            <Schedule fontSize="small" />
            <Typography variant="body2">
              Starts: {tournament.start_date ? new Date(tournament.start_date).toLocaleDateString() : 'TBD'}
            </Typography>
          </Box>

          <Box display="flex" alignItems="center" gap={1} mb={2}>
            <People fontSize="small" />
            <Typography variant="body2">
              Tournament Format: {tournament.format || 'round_robin'}
            </Typography>
          </Box>

          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Box>
              <Chip 
                label={tournament.status === 'registration' ? 'Open for Registration' : 'In Progress'} 
                color={tournament.status === 'registration' ? 'success' : 'primary'} 
                size="small" 
              />
            </Box>
            <Box>
              {tournament.status === 'registration' && (
                <Button
                  variant="contained"
                  size="small"
                  onClick={() => handleJoinTournament(tournament.id)}
                >
                  Join Tournament
                </Button>
              )}
              {tournament.status === 'active' && (
                <Button
                  variant="outlined"
                  size="small"
                  color="error"
                  onClick={() => handleLeaveTournament(tournament.id)}
                >
                  Leave Tournament
                </Button>
              )}
            </Box>
          </Box>
        </CardContent>
      </Card>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
          Tournaments
        </Typography>
          <Typography variant="body1" color="text.secondary">
            Compete in tournaments and climb the rankings
          </Typography>
        </Box>
      </Box>

      {/* Error Display */}
      {error && (
        <Box sx={{ mb: 2 }}>
          <Typography color="error" variant="body2">
            {error}
          </Typography>
          <Button onClick={fetchTournaments} startIcon={<Refresh />} size="small">
            Retry
          </Button>
        </Box>
      )}

      {/* Search and Filter */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" flexDirection={{ xs: 'column', sm: 'row' }} gap={2} alignItems="center">
          <Box flexGrow={1}>
            <TextField
              fullWidth
              placeholder="Search tournaments..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
              }}
            />
          </Box>
          <Box minWidth={200}>
            <FormControl fullWidth>
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                label="Status"
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="registration">Registration Open</MenuItem>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="cancelled">Cancelled</MenuItem>
              </Select>
            </FormControl>
          </Box>
          <Box>
            <Button
              fullWidth
              variant="outlined"
              startIcon={<Refresh />}
              onClick={fetchTournaments}
            >
              Refresh
            </Button>
          </Box>
        </Box>
      </Paper>

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={(e, newValue) => setTabValue(newValue)}>
          <Tab 
            label={
              <Badge badgeContent={openTournaments.length} color="primary">
                Open Tournaments
              </Badge>
            } 
          />
          <Tab 
            label={
              <Badge badgeContent={myTournaments.length} color="secondary">
                My Tournaments
              </Badge>
            } 
          />
          <Tab label="All Tournaments" />
        </Tabs>
      </Paper>

      {/* Tab Panels */}
      <TabPanel value={tabValue} index={0}>
        {openTournaments.length === 0 ? (
          <Box textAlign="center" py={4}>
            <EmojiEvents sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No open tournaments
            </Typography>
            <Typography color="text.secondary" paragraph>
              Check back later for new tournaments or create one if you're an admin!
            </Typography>
          </Box>
        ) : (
          openTournaments.map(renderTournamentCard)
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        {myTournaments.length === 0 ? (
          <Box textAlign="center" py={4}>
            <EmojiEvents sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No tournaments created
            </Typography>
            <Typography color="text.secondary" paragraph>
              Create tournaments to manage them here!
            </Typography>
                  </Box>
        ) : (
          myTournaments.map(renderTournamentCard)
        )}
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        {allTournaments.length === 0 ? (
          <Box textAlign="center" py={4}>
            <EmojiEvents sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" gutterBottom>
              No tournaments found
            </Typography>
            <Typography color="text.secondary" paragraph>
              Try adjusting your search criteria or create a new tournament.
            </Typography>
            </Box>
        ) : (
          allTournaments.map(renderTournamentCard)
        )}
      </TabPanel>

      {/* Create Tournament Dialog */}
      <Dialog open={openTournamentDialog} onClose={() => setOpenTournamentDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Create New Tournament</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" paragraph>
            Tournament creation form would go here. This would include tournament details, 
            format selection, scheduling, and participant management.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenTournamentDialog(false)}>Cancel</Button>
          <Button variant="contained">Create Tournament</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}; 
